<template>
  <div class="relative w-full flex flex-row gap-3">
    <button
      type="button"
      class="place-items-center h-10 w-10 rounded-full bg-white/80 shadow hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed my-auto"
      :disabled="atStart"
      @click="prev"
    >
      <SimpleArrowLeft />
    </button>

    <div ref="viewport" class="overflow-hidden w-full">
      <div
        class="flex transition-transform duration-300 ease-out"
        :style="trackStyle"
        role="group"
        aria-roledescription="carousel"
      >
        <component
          :is="vnode"
          v-for="(vnode, i) in items"
          :key="i"
          class="shrink-0"
          :style="{ width: itemWidth }"
          :aria-roledescription="'slide'"
          :aria-label="`Item ${i + 1} von ${count}`"
        />
      </div>
    </div>

    <button
      type="button"
      class="place-items-center h-10 w-10 rounded-full bg-white/80 shadow hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed my-auto"
      :disabled="atEnd"
      @click="next"
    >
      <SimpleArrowRight />
    </button>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  useSlots,
  watch,
  onMounted,
  onBeforeUnmount,
  Fragment,
} from "vue";
import SimpleArrowLeft from "~/components/v2/icon/SimpleArrowLeft.vue";
import SimpleArrowRight from "~/components/v2/icon/SimpleArrowRight.vue";

const props = defineProps({
  initialIndex: { type: Number, default: 0 },
  itemsPerView: { type: Number, default: 3, validator: (v) => v > 0 },
  // Gap in px between items
  gap: { type: Number, default: 16 },
});

const slots = useSlots();
const current = ref(props.initialIndex);

function flatten(nodes) {
  const out = [];
  for (const n of nodes || []) {
    if (!n) continue;
    if (n.type === Fragment && Array.isArray(n.children)) {
      out.push(...flatten(n.children));
    } else {
      out.push(n);
    }
  }
  return out;
}

const rawNodes = computed(() => (slots.default ? slots.default() : []));
const items = computed(() => flatten(rawNodes.value));
const count = computed(() => items.value.length);

const viewport = ref(null);
const viewportWidth = ref(0);
let ro;

onMounted(() => {
  const el = viewport.value;
  const update = () => {
    viewportWidth.value = el?.clientWidth || 0;
  };
  update();
  ro = new ResizeObserver(update);
  if (el) ro.observe(el);
});

onBeforeUnmount(() => {
  if (ro) ro.disconnect();
});

const effectiveItemsPerView = computed(() =>
  viewportWidth.value < 658 ? 1 : props.itemsPerView
);

const itemWidthPx = computed(() => {
  const gapsTotal = (effectiveItemsPerView.value - 1) * props.gap;
  return effectiveItemsPerView.value > 0
    ? (viewportWidth.value - gapsTotal) / effectiveItemsPerView.value
    : 0;
});

const itemWidth = computed(
  () =>
    `calc((100% - ${(effectiveItemsPerView.value - 1) * props.gap}px) / ${effectiveItemsPerView.value})`
);

const pages = computed(() => {
  if (count.value === 0) return 1;
  return Math.max(1, count.value - effectiveItemsPerView.value + 1);
});

const page = computed(() => Math.min(current.value, pages.value - 1));

watch([count, () => effectiveItemsPerView], () => {
  const maxStart = Math.max(0, count.value - effectiveItemsPerView.value);
  if (current.value > maxStart) current.value = maxStart;
});

const atStart = computed(() => current.value <= 0);
const atEnd = computed(
  () => current.value >= Math.max(0, count.value - effectiveItemsPerView.value)
);

const offsetPx = computed(
  () => current.value * (itemWidthPx.value + props.gap)
);

const trackStyle = computed(() => ({
  transform: `translateX(-${offsetPx.value}px)`,
  gap: `${props.gap}px`,
}));

function prev() {
  if (!atStart.value) current.value -= 1;
}
function next() {
  if (!atEnd.value) current.value += 1;
}
</script>
